import { afterNextRender, DestroyRef, effect, inject, NgZone, Signal, signal } from '@angular/core';

// Sub-pixel text metrics make scrollWidth exceed clientWidth by a fraction on
// text that visually fits, so only a whole pixel counts as cut off.
const TRUNCATION_TOLERANCE_PX = 1;

/**
 * Tracks whether an element's text is cut off, re-measuring whenever the element
 * resizes, its text changes, or its styling changes.
 *
 * Knows nothing about tooltips: it reports a state, and the caller decides what to
 * do with it — suppress a tooltip, swap an icon, expand a row.
 *
 * Must be called from an injection context.
 *
 * @param element the element whose text may be cut off
 * @param enabled while this reads `false`, nothing is observed and the state is `false`
 */
export const useTruncation = (element: HTMLElement, enabled?: Signal<boolean>): Signal<boolean> => {
  const ngZone = inject(NgZone);
  const destroyRef = inject(DestroyRef);

  const isTruncated = signal(false);

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;

  const measure = () => {
    isTruncated.set(element.scrollWidth - element.clientWidth > TRUNCATION_TOLERANCE_PX);
  };

  const disconnect = () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    mutationObserver?.disconnect();
    mutationObserver = null;
  };

  const observe = () => {
    // zone.js patches MutationObserver, so its callbacks would otherwise trigger an
    // app-wide change-detection tick on every text or styling mutation of a watched
    // element. Nothing here needs the zone: the state is published by writing a
    // signal, which schedules exactly the change detection required — under zoneless
    // too, where this wrapper degrades to a plain call.
    ngZone.runOutsideAngular(() => {
      if (globalThis?.ResizeObserver && !resizeObserver) {
        resizeObserver = new globalThis.ResizeObserver(() => measure());
        resizeObserver.observe(element);
      }

      if (!mutationObserver) {
        mutationObserver = new MutationObserver(() => measure());
        // class/style are watched because styling can change how wide the text
        // renders without changing the element's box, which a ResizeObserver
        // never reports. The filter is deliberate: a tooltip on this element
        // writes aria-describedby to it, so observing every attribute would feed
        // those writes back into the measurement.
        mutationObserver.observe(element, {
          characterData: true,
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    });
  };

  const sync = () => {
    if (enabled && !enabled()) {
      disconnect();
      isTruncated.set(false);
      return;
    }

    observe();
    measure();
  };

  // Nothing is measurable until the element is laid out.
  afterNextRender(() => sync());

  effect(() => sync());

  destroyRef.onDestroy(disconnect);

  return isTruncated.asReadonly();
};
